import { z } from "zod";

const ledgerId = z.string().regex(/^[1-9]\d*$/).refine(v => BigInt(v) <= 9223372036854775807n, "Ledger id is out of range");
export const mainxExpenseShareBody = z.strictObject({
  side: z.enum(["in_house", "vendor", "maintainx_unsplit"]),
  vendorId: z.uuid().nullable(),
  amount: z.string().regex(/^[0-9]{1,8}(\.[0-9]{1,2})?$/).refine(v => !/^0+(\.0+)?$/.test(v), "Amount must be positive"),
}).superRefine((v, ctx) => {
  if ((v.side === "vendor" && !v.vendorId) || (v.side === "maintainx_unsplit" && v.vendorId))
    ctx.addIssue({ code: "custom", message: "Choose a vendor for vendor spend; undecided money cannot name a vendor" });
});
export const mainxExpenseDecisionBody = z.strictObject({
  shares: z.array(mainxExpenseShareBody).max(100),
  expenseRows: z.array(z.strictObject({ row: z.number().int().min(0), side: z.enum(["in_house", "vendor", "maintainx_unsplit"]), vendorId: z.uuid().nullable() })).max(1000).nullable().default(null),
  note: z.string().trim().max(1000).nullable().default(null),
  expectedDecisionId: ledgerId.nullable(),
});
/**
 * The review list's tabs (Eden, 9 October 2026: the list shows only what is
 * left, with the rest one tab away):
 *   - `left`     some of the line's money is still not yet split: neither the
 *                vendor rule nor the name match placed it, and no person has;
 *   - `matched`  no person's decision, and the name match placed some of it;
 *   - `decided`  a person's decision with no money left undecided;
 *   - `all`      every imported expense line.
 * `undecided` and `multiple` are the filters before 9 October, kept so an old
 * link or CSV export still answers.
 */
export const mainxExpenseReviewStatuses = ["left", "matched", "decided", "all", "undecided", "multiple"] as const;
export type MainxExpenseReviewStatus = (typeof mainxExpenseReviewStatuses)[number];
export const mainxExpenseReviewQuery = z.strictObject({
  status: z.enum(mainxExpenseReviewStatuses).default("left"),
  vendorId: z.uuid().optional(),
  minimumAmount: z.string().regex(/^[0-9]{1,8}(\.[0-9]{1,2})?$/).optional(),
});
export const mainxExpenseLineId = ledgerId;
export type MainxExpenseDecisionBody = z.infer<typeof mainxExpenseDecisionBody>;
export type MainxExpenseReviewQuery = z.infer<typeof mainxExpenseReviewQuery>;
export type MainxExpenseShare = z.infer<typeof mainxExpenseShareBody>;
export interface MainxReviewVendor { id: string; name: string; repair: boolean; parts: boolean; externalService: boolean }
export interface MainxExpenseRow { kind: string; description?: string; descriptionHeld?: boolean; amount?: string; quantity?: string; unitCost?: string }
/**
 * One MaintainX expense row whose description names exactly one vendor
 * (`v_mainx_expense_name_match`). `expenseRow` counts from 1 in the record's
 * order, the same order as `expenseRows`. `side` is what the vendor's role
 * gives: vendor for a service provider, in_house for a parts-only supplier,
 * null for a vendor with no role (the row then stays not yet split).
 */
export interface MainxExpenseNameMatch {
  expenseRow: number; description: string | null; amount: string;
  vendorId: string; vendorName: string; side: "vendor" | "in_house" | null;
}
export interface MainxExpenseReviewLine {
  costEntryId: string; workOrderId: string; number: string; mainxNumber: string | null;
  title: string; unit: string | null; date: string; currency: string; amount: string;
  latestDecisionId: string | null; latestHasShares: boolean; serviceVendorCount: number; realVendorCount: number;
  /**
   * An invoice or receipt is on the job: attached and not detached, classed or
   * attached as invoice/receipt, not failed or quarantined, not a void invoice's
   * file. On an OPEN job it gates the vendor-role default (Eden, 9 October
   * 2026): without it a sole vendor's money is not yet split and `ruleVendorId`
   * is null. A closed job does not need it.
   */
  hasCostEvidence: boolean;
  /** The Work orders page's boundary: completed or cancelled, read live. */
  workOrderClosed: boolean;
  ruleSide: string; ruleVendorId: string | null;
  /** The job's only real vendor, whether or not the rule may name it. */
  soleVendorId: string | null;
  /** The job's vendors and any vendor its expense rows name. */
  vendors: MainxReviewVendor[];
  /**
   * The rows' name matches, listed only where the match may apply: a closed
   * job the rule leaves not yet split. `nameMatchApplied` says whether the
   * figures use them (they do not when the rows do not add up to this line, or
   * a person's decision has shares).
   */
  nameMatches: MainxExpenseNameMatch[]; nameMatchApplied: boolean;
  currentShares: MainxExpenseShare[]; expenseRows: MainxExpenseRow[]; documentCount: number;
}
/** How many lines each tab holds, under the same vendor and amount filters. */
export interface MainxExpenseReviewCounts { left: number; matched: number; decided: number; all: number }
export interface MainxExpenseReviewResponse { data: MainxExpenseReviewLine[]; counts: MainxExpenseReviewCounts }
export interface MainxExpenseReviewDetail {
  comments: { body: string; at: string }[];
  history: { id: string; at: string; reviewer: string; note: string | null; shares: MainxExpenseShare[] }[];
}
